import MenusService from '@/api/services/menus.service';
import {
  buildExcelExport,
  capSheetRows,
} from '../../exports/gpthelp-excel-builder';
import type {
  IGpthelpExcelBuildInput,
  IGpthelpExcelBuildOutput,
} from '../../exports/gpthelp-excel-builder';
import { registerExport } from '../../exports/gpthelp-export-registry';
import {
  catalogExportFilename,
  catalogExportNeedsMenuNames,
  catalogExportSheets,
  fileTranslator,
} from '../../exports/catalog-export';
import type { IGhCalloutCard } from '../../gpthelp-host-contract';
import { cardId } from '../../cards/card-ids';
import type { ICardFormat } from '../../cards/card-text';
import { exportReadyCard } from '../../cards/export-cards';
import { HostToolError } from '../tool-types';
import type { IGpthelpTool, IGpthelpToolGate } from '../tool-types';
import {
  getOptionalBoolean,
  getOptionalNumber,
  getOptionalNumberArray,
  getOptionalString,
  parseExcelSheetsOrThrow,
} from '../tool-args';
import { toolResultEnvelope } from '../tool-envelope';
import { runToolApiCall } from '../tool-http';
import { switchedOff } from '../tool-switch';
import { buildMenuToolHelpers } from './menu-tools';

/** Categories one menu export may name. */
const EXPORT_CATEGORY_IDS_MAX = 100;

const EXPORT_NOTE =
  'The user downloads the file from the card. One short sentence.';

const GENERATE_EXCEL_DESCRIPTION =
  'Generate an Excel (.xlsx) export from structured data; the user downloads it from the card this shows. DO NOT USE THIS TOOL UNLESS THE USER ASKED TO EXPORT THE DATA.';

/** export_generateExcel's pointer to the menu export (plan P1-7, F10 naming rule). */
const MENU_EXPORT_SENTENCE =
  'For the menu or a price list use menu_exportCatalog.';

/** Every member reads the menu, so every member can export it. */
const menuExportAvailable = (gate: IGpthelpToolGate): boolean =>
  gate.hasRestaurantScope;

/**
 * Builds the workbook, keeps it in this browser for its «Descargar» and
 * returns the card that offers it.
 */
function prepareExport(
  input: IGpthelpExcelBuildInput,
  fmt: ICardFormat
): { exportData: IGpthelpExcelBuildOutput; card: IGhCalloutCard } {
  let exportData: IGpthelpExcelBuildOutput;
  try {
    exportData = buildExcelExport(input);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new HostToolError('EXPORT_FAILED', msg);
  }

  const exportId = registerExport({
    filename: exportData.filename,
    mime: exportData.mime,
    bytes: exportData.bytes,
    meta: exportData.meta,
  });

  return {
    exportData,
    card: exportReadyCard(
      {
        id: cardId('export', exportId),
        exportId,
        filename: exportData.filename,
      },
      fmt
    ),
  };
}

const generateExcelDefinition: IGpthelpTool['definition'] = {
  name: 'export_generateExcel',
  description: GENERATE_EXCEL_DESCRIPTION,
  // Builds a file in this browser: nothing in the store changes.
  sideEffects: 'none',
  parameters: {
    type: 'object',
    additionalProperties: false,
    properties: {
      filename: {
        type: 'string',
        description:
          'File name (without path). If extension is omitted, .xlsx will be added.',
      },
      sheets: {
        type: 'array',
        description:
          'Sheets to include. Each sheet defines columns and rows. Max 5 sheets, 50 columns, 20k rows per sheet.',
        items: {
          type: 'object',
          additionalProperties: false,
          properties: {
            name: { type: 'string' },
            columns: {
              type: 'array',
              items: {
                type: 'object',
                additionalProperties: false,
                properties: {
                  key: { type: 'string' },
                  header: { type: 'string' },
                  type: {
                    type: 'string',
                    enum: ['string', 'number', 'date', 'datetime'],
                  },
                },
                required: ['key', 'header'],
              },
            },
            rows: {
              type: 'array',
              items: { type: 'object' },
            },
          },
          required: ['name', 'columns', 'rows'],
        },
      },
    },
    required: ['sheets'],
  },
};

export const exportTools: IGpthelpTool[] = [
  {
    definition: {
      name: 'menu_exportCatalog',
      description:
        'Builds an Excel file of the menu from the live catalog; the user downloads it from the card. One row per product (one per size), with category, prices, discounted prices, availability in this branch, what customers see now, description and last price change, plus an options sheet. Use it for ANY request to export, download or print the menu or a price list; never copy menu rows into another export.',
      // Builds a file in this browser: nothing in the store changes.
      sideEffects: 'none',
      parameters: {
        type: 'object',
        additionalProperties: false,
        properties: {
          menuId: {
            type: 'integer',
            description:
              'Only this menu (non-default menus only; for the default menu pass its categoryIds).',
          },
          categoryIds: {
            type: 'array',
            items: { type: 'integer' },
            maxItems: EXPORT_CATEGORY_IDS_MAX,
            description: 'Only these categories.',
          },
          includeOptions: {
            type: 'boolean',
            default: true,
            description: 'Adds the options sheet.',
          },
        },
      },
    },
    policy: 'read',
    isAvailable: menuExportAvailable,
    async execute(args, { deps, scope, fmt }) {
      const menuIdRaw = getOptionalNumber(args, 'menuId');
      const menuId = menuIdRaw === null ? null : Math.floor(menuIdRaw);
      const categoryIds =
        getOptionalNumberArray(
          args,
          'categoryIds',
          EXPORT_CATEGORY_IDS_MAX
        )?.map((id) => Math.floor(id)) ?? null;
      const includeOptions = getOptionalBoolean(args, 'includeOptions') ?? true;

      // Everything of the store is read before the menu list request, so the
      // file stays this store's even if the member switches stores meanwhile.
      const helpers = buildMenuToolHelpers(deps);
      const snapshot = helpers.getMenuCatalogSnapshotOrThrow();
      const { availableMenus } = deps.menuStore;
      const branchName = deps.restaurantStore.selectedBranch?.name ?? '';
      const slug = deps.restaurantStore.restaurant?.stringId ?? '';
      // A file with the menu column names its menus from the Menú page's menu
      // list, also those no order type uses.
      const storeMenus = catalogExportNeedsMenuNames(
        snapshot.categories,
        availableMenus
      )
        ? await runToolApiCall(() =>
            new MenusService().getAll(scope.restaurantId)
          )
        : [];
      const t = fileTranslator(deps.i18n);
      const sheets = catalogExportSheets({
        categories: snapshot.categories,
        items: snapshot.items,
        availableMenus,
        storeMenus: Array.isArray(storeMenus) ? storeMenus : [],
        ctx: helpers.visibilityCtx,
        branchName,
        menuId,
        categoryIds,
        includeOptions,
        timeZone: scope.timeZone,
        t,
      });
      if (sheets[0].rows.length === 0) {
        throw new HostToolError(
          'NOT_FOUND',
          'No products to export: the menu is empty, or menuId/categoryIds left every product out',
          { menuId, categoryIds }
        );
      }

      // A workbook holds 20,000 rows per sheet: a bigger menu is cut.
      const capped = capSheetRows(sheets);
      const { exportData, card } = prepareExport(
        {
          filename: catalogExportFilename({
            slug,
            t,
            now: new Date(),
            timeZone: scope.timeZone,
          }),
          sheets: capped.sheets,
          // The store's own text: «+ Bacon» and «- 2 empanadas» stay as written.
          formulaGuard: 'storeText',
        },
        fmt
      );

      return toolResultEnvelope({
        data: {
          ok: true,
          filename: exportData.filename,
          rows: {
            products: capped.sheets[0].rows.length,
            options: capped.sheets[1]?.rows.length ?? 0,
          },
          ...(capped.truncated ? { truncated: true } : {}),
          note: EXPORT_NOTE,
        },
        cards: [card],
      });
    },
  },
  {
    definition: generateExcelDefinition,
    // Names the menu export only while this member is offered it (plan F10).
    definitionFor: (gate) =>
      menuExportAvailable(gate) && !switchedOff('menu_exportCatalog', gate)
        ? {
            ...generateExcelDefinition,
            description: `${GENERATE_EXCEL_DESCRIPTION} ${MENU_EXPORT_SENTENCE}`,
          }
        : generateExcelDefinition,
    policy: 'read',
    isAvailable: (gate) => gate.hasRestaurantScope,
    execute(args, { fmt }) {
      const filenameRaw = getOptionalString(args, 'filename') ?? 'export';
      const sheets = parseExcelSheetsOrThrow(args.sheets);
      const { exportData, card } = prepareExport(
        { filename: filenameRaw, sheets },
        fmt
      );

      return toolResultEnvelope({
        data: {
          ok: true,
          filename: exportData.filename,
          meta: exportData.meta,
          note: EXPORT_NOTE,
        },
        cards: [card],
      });
    },
  },
];
